/**
 * @file AI Gallery 发布相关 API Hooks
 * 
 * 包含以下接口：
 * - 获取标签列表
 * - 创建自定义标签
 * - 搜索用户
 * - 创建作品
 */
import { useCallback } from 'react';
import { useRequestController } from '../../../../Home/hooks/useRequestController.ts';

// ==============================
// 类型定义
// ==============================

/** 标签项接口（内部使用，统一格式） */
export interface IAiGalleryTagItem {
  /** 标签 ID */
  id: number;
  /** 标签名称（兼容旧代码，优先取中文） */
  name: string;
  /** 中文名称 */
  name_cn: string;
  /** 英文名称 */
  name_en: string;
}

/** API 返回的标签项原始格式 */
interface IAiGalleryTagItemRaw {
  /** 标签 ID */
  id: number;
  /** 中文名称 */
  name_cn: string;
  /** 英文名称 */
  name_en: string;
  /** 图标类名 */
  icon?: string;
  /** 标签类型 */
  type?: string;
  /** 关联作品数 */
  count?: number;
}

/** 获取标签列表 API 原始响应 */
interface IAiGalleryTagsResponseRaw {
  /** 标签列表 */
  items: IAiGalleryTagItemRaw[];
}

/** 获取标签列表响应（转换后） */
export interface IAiGalleryTagsResponse {
  /** 标签列表 */
  tags: IAiGalleryTagItem[];
}

/** 创建自定义标签请求参数 */
export interface IAiGalleryCreateTagParams {
  /** 中文标签名 (1-10)，必填 */
  name_cn: string;
  /** 英文标签名 (1-20)，必填 */
  name_en: string;
  /** RemixIcon 类名，可选，默认 ri-price-tag-3-fill */
  icon?: string;
}

/** 创建自定义标签响应 */
export interface IAiGalleryCreateTagResponse {
  /** 新建标签 ID */
  id: number;
  /** 中文标签名 */
  name_cn: string;
  /** 英文标签名 */
  name_en: string;
  /** 图标类名 */
  icon: string;
  /** 标签类型，恒为 custom */
  type: 'custom';
  /** 关联作品数，新建时恒为 0 */
  count: number;
}

/** 用户项接口 */
export interface IAiGalleryUserItem {
  /** 用户 openid（用于 share_users） */
  openid: string;
  /** 用户名 */
  username: string;
  /** 邮箱（用于显示） */
  email?: string;
  /** 头像 URL */
  avatar?: string;
}

/** 标签引用（API 提交格式） */
export interface IAiGalleryTagRef {
  /** 标签 ID */
  id: number;
}

/** 来源类型 */
export type TAiGallerySource = 'link' | 'file';

/** 访问权限类型 */
export type TAiGalleryVisibility = 'self' | 'user' | 'public';

/** 创建作品请求参数 */
export interface IAiGalleryCreateParams {
  /** 作品中文名 (1-40)，必填 */
  name_cn: string;
  /** 作品英文名 (1-60)，必填 */
  name_en: string;
  /** 中文描述 (0-200) */
  desc_cn?: string;
  /** 英文描述 (0-300) */
  desc_en?: string;
  /** 来源类型：link / file，必填 */
  source: TAiGallerySource;
  /** 外部链接，source=link 时必填，必须 https://，长度 ≤ 2048 */
  link?: string;
  /** HTML 文件，source=file 时必填 */
  file?: File;
  /** 标签列表 (1-5 个)，必填 */
  tags: IAiGalleryTagRef[];
  /** 访问权限，必填 */
  visibility: TAiGalleryVisibility;
  /** 分享用户 openid 列表，visibility=user 时至少 1 个 */
  share_users?: string[];
  /** 数据类型：real=真实数据，demo=Demo 数据，默认 demo */
  data_mode?: 'real' | 'demo';
}

/** 创建作品响应 */
export interface IAiGalleryCreateResponse {
  /** 作品 ID */
  id: string;
  /** 规则 ID */
  rule_id?: number;
  /** 规则 Key（UUID，用于拼装 chatShare 链接的 snapshotId） */
  rule_key?: string;
  /** 访问 URL（后端直接返回时优先使用） */
  url?: string;
}

// ==============================
// Hooks
// ==============================

/**
 * 获取标签列表 Hook
 * 
 * @example
 * const { data, loading, run } = useAiGalleryTags();
 * run(); // 手动触发请求
 * const tags = data?.tags || [];
 */
export const useAiGalleryTags = () => {
  const requestData = useRequestController({
    url: '/api/ai-gallery/tags',
    method: 'GET',
    manual: true,
  });

  const rawData = requestData.data as IAiGalleryTagsResponseRaw | undefined;
  const tags: IAiGalleryTagItem[] = rawData?.items?.map(item => ({
    id: item.id,
    name: item.name_cn || item.name_en || `Tag-${item.id}`,
    name_cn: item.name_cn || item.name_en || `Tag-${item.id}`,
    name_en: item.name_en || item.name_cn || `Tag-${item.id}`,
  })) || [];

  return {
    ...requestData,
    data: { tags } satisfies IAiGalleryTagsResponse,
  };
};

/**
 * 创建自定义标签 Hook
 * 
 * @example
 * const { data: tagsData, run: fetchTags } = useAiGalleryTags();
 * const { createTag, loading } = useAiGalleryCreateTag();
 * const result = await createTag({ name_cn: '我的标签', name_en: 'My Tag' });
 * if (result) fetchTags(); // 创建成功后刷新列表
 */
export const useAiGalleryCreateTag = () => {
  const requestData = useRequestController({
    url: '/api/ai-gallery/tags',
    method: 'POST',
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
      errorHandler: true,
    },
  });

  /** 创建自定义标签 */
  const createTag = useCallback(
    async (params: IAiGalleryCreateTagParams): Promise<IAiGalleryCreateTagResponse | null> => {
      try {
        const result = await requestData.runAsync(params);
        // rawData=true 时返回原始响应 {code, message, data}
        if ((result as any)?.code === 0 && (result as any)?.data) {
          return (result as any).data as IAiGalleryCreateTagResponse;
        }
        return null;
      } catch (err: any) {
        // useRequestController 可能把成功响应当异常抛出
        if (err?.code === 0 && err?.data) {
          return err.data as IAiGalleryCreateTagResponse;
        }
        return null;
      }
    },
    [requestData.runAsync],
  );

  return {
    loading: requestData.loading,
    createTag,
  };
};

/**
 * 搜索用户 Hook
 * 
 * @example
 * const { searchUsers, loading } = useAiGalleryUserSearch();
 * const users = await searchUsers('张三');
 * // users[0].openid 用于 share_users
 */
export const useAiGalleryUserSearch = () => {
  const requestData = useRequestController({
    url: '/api/ai-gallery/users/search',
    method: 'GET',
    manual: true,
  });

  /** 搜索用户，返回用户列表（包含 openid） */
  const searchUsers = useCallback(
    async (keyword: string): Promise<IAiGalleryUserItem[]> => {
      if (!keyword?.trim()) return [];
      try {
        const result = await requestData.runAsync({ keyword: keyword.trim() });
        // 接口返回 { items: [...] }，兼容 users 字段
        const raw = result as Record<string, unknown>;
        const items = (raw?.items || raw?.users || []) as IAiGalleryUserItem[];
        return items;
      } catch {
        return [];
      }
    },
    [requestData.runAsync],
  );

  return {
    loading: requestData.loading,
    searchUsers,
  };
};

/**
 * 创建作品 Hook（multipart/form-data）
 * 
 * @example
 * const { createGallery, loading } = useAiGalleryCreate();
 * const result = await createGallery({
 *   name_cn: '作品中文名',
 *   name_en: 'English Name',
 *   source: 'file',
 *   file: htmlFile,
 *   tags: [{ id: 1 }, { id: 2 }],
 *   visibility: 'self',
 * });
 */
export const useAiGalleryCreate = () => {
  const requestData = useRequestController({
    url: '/api/ai-gallery/dashboards',
    method: 'POST',
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
      errorHandler: true,
    },
  });

  /** 将参数构建为 FormData 并创建作品 */
  const createGallery = useCallback(
    async (params: IAiGalleryCreateParams): Promise<IAiGalleryCreateResponse | null> => {
      try {
        const formData = new FormData();
        formData.append('name_cn', params.name_cn);
        formData.append('name_en', params.name_en);
        if (params.desc_cn) formData.append('desc_cn', params.desc_cn);
        if (params.desc_en) formData.append('desc_en', params.desc_en);
        formData.append('source', params.source);
        if (params.link) formData.append('link', params.link);
        if (params.file) formData.append('file', params.file);
        formData.append('tags', JSON.stringify(params.tags));
        formData.append('visibility', params.visibility);
        if (params.share_users && params.share_users.length > 0) {
          formData.append('share_users', JSON.stringify(params.share_users));
        }
        if (params.data_mode) {
          formData.append('data_mode', params.data_mode);
        }

        const result = await requestData.runAsync(formData);
        // rawData=true + errorHandler=true：
        // 正常分支(status=200)返回原始 {code, message, data}
        // 非200状态码(如201 Created)会被 reject，在 catch 中处理
        if ((result as any)?.code === 0 && (result as any)?.data) {
          return (result as any).data as IAiGalleryCreateResponse;
        }
        return null;
      } catch (err: any) {
        // API 返回 201 状态码时，http.ts 因 status!==200 走 default 分支 reject
        // 但 errorHandler=true 阻止了全局 "System Error." 弹出
        // reject 的值是原始响应 data: {code, message, data}
        if (err?.code === 0 && err?.data) {
          return err.data as IAiGalleryCreateResponse;
        }
        return null;
      }
    },
    [requestData.runAsync],
  );

  return {
    loading: requestData.loading,
    createGallery,
  };
};
